/**
 * Date handling for self-registration.
 *
 * Both helpers used to mix UTC parsing with local-time getters, which shifted every
 * date of birth back by one day in any negative UTC offset. Texas is UTC-5/-6, so at
 * Alamo it was every registration, every time:
 *
 *   new Date('1985-03-15')   // JS parses a DATE-ONLY string as UTC midnight
 *     .getDate()             // local getter -> 14, because UTC midnight is 19:00 the
 *                            //                previous day in America/Chicago
 *
 * The shifted value reached the submitted form, so a returning patient no longer
 * matched their existing record on date of birth and was given a new MRN.
 */

/**
 * Normalise a date of birth into the `YYYY-MM-DD` form `<input type="date">` expects.
 *
 * A date-only string is already in that form and is returned as written — it is never
 * round-tripped through `Date`, which is what introduced the offset. Anything else is
 * read with UTC getters, matching how JS parses a date-only value in the first place.
 */
export function toDateInputValue(input: unknown): string {
  if (input === undefined || input === null || input === '') return '';

  if (typeof input === 'string') {
    const dateOnly = input.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnly) return dateOnly[1];
  }

  const d = input instanceof Date ? input : new Date(input as string | number);
  if (Number.isNaN(d.getTime())) return '';

  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/**
 * True when the patient is under 18, which makes the guarantor fields required.
 *
 * Compares calendar parts in one frame. Previously the birth date came from a
 * UTC-parsed `Date` read with local getters while `today` was plain local, so the
 * day-of-month comparison was off by one and a patient could read as a minor on or
 * around their eighteenth birthday — which then demanded guarantor details they had
 * no reason to supply, and blocked the form.
 *
 * `today` is injectable so the boundary can be tested without faking the clock.
 */
export function isMinor(dateOfBirth: unknown, today: Date = new Date()): boolean {
  const dob = toDateInputValue(dateOfBirth);
  if (!dob) return false;

  const [year, month, day] = dob.split('-').map(Number);
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() + 1 - month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age -= 1;
  }
  return age < 18;
}
